import { useEffect, useState } from "react";
import { menu as menuApi, client } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Save, RefreshCw } from "lucide-react";

const CATEGORIES = [
  "vodka", "tequila", "gin", "cognac", "rum", "scotch",
  "whiskey_bourbon", "champagne", "other", "packages", "ultra_experience",
];
const EMPTY = { name: "", description: "", price: 0, category: "vodka", image_url: "", coming_soon: false, order: 0 };

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [reseeding, setReseeding] = useState(false);

  const load = () => menuApi.list().then(setItems);
  useEffect(() => { load(); }, []);

  const start = (it) => { setEditing(it?.id || "new"); setForm(it ? { ...it } : EMPTY); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };
  const save = async () => {
    try {
      if (editing === "new") await menuApi.create(form);
      else await menuApi.update(editing, form);
      toast.success("Saved"); cancel(); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await menuApi.remove(id); load(); };

  const reseed = async () => {
    if (!window.confirm("This will REPLACE the entire menu with the current bottle list from the codebase. Any custom items you added in the admin will be removed. Continue?")) return;
    setReseeding(true);
    try {
      const res = await client.post("/admin/reseed-menu");
      toast.success(`Menu reseeded — ${res.data.inserted} items live`);
      load();
    } catch (e) {
      toast.error("Reseed failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setReseeding(false);
    }
  };

  return (
    <div data-testid="admin-menu">
      <div className="flex justify-between items-center mb-8">
        <div><span className="eyebrow">— Menu</span><h1 className="font-display text-4xl mt-2"><span className="italic text-gold">Menu</span> Items</h1></div>
        <div className="flex gap-2">
          <button
            onClick={reseed}
            disabled={reseeding}
            data-testid="reseed-menu-btn"
            className="btn-luxe btn-luxe-ghost disabled:opacity-50"
            title="Replace the menu with the latest bottle list from the codebase"
          >
            <RefreshCw size={14} className={reseeding ? "animate-spin" : ""} />
            {reseeding ? "Reseeding…" : "Reseed Menu"}
          </button>
          <button onClick={() => start(null)} className="btn-luxe"><Plus size={14} /> New Item</button>
        </div>
      </div>

      {editing && (
        <div className="glass p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name"><input className="luxe-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Price"><input type="number" step="0.01" className="luxe-input" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} /></Field>
            <Field label="Category">
              <select className="luxe-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
              </select>
            </Field>
            <Field label="Order"><input type="number" className="luxe-input" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></Field>
            <Field label="Description" full><textarea rows={2} className="luxe-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 mt-4 text-sm text-white/70"><input type="checkbox" checked={form.coming_soon} onChange={e => setForm({ ...form, coming_soon: e.target.checked })} /> Coming Soon</label>
          <div className="flex gap-3 mt-6">
            <button onClick={save} className="btn-luxe btn-luxe-solid"><Save size={14} /> Save</button>
            <button onClick={cancel} className="btn-luxe btn-luxe-ghost">Cancel</button>
          </div>
        </div>
      )}

      {CATEGORIES.map(cat => (
        <section key={cat} className="mb-10">
          <h2 className="font-display text-2xl text-gold capitalize mb-3">{cat.replace("_", " ")}</h2>
          <div className="border border-white/5">
            {items.filter(i => i.category === cat).map(i => (
              <div key={i.id} className="flex justify-between items-center p-4 border-b border-white/5 last:border-b-0 hover:bg-[var(--onyx-charcoal)]">
                <div>
                  <p className="font-display text-lg">{i.name} {i.coming_soon && <span className="ml-2 text-[10px] text-gold tracking-luxe uppercase">Coming Soon</span>}</p>
                  <p className="text-xs text-white/50 font-light">{i.description}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-gold font-display text-lg">${i.price.toFixed(0)}</span>
                  <button onClick={() => start(i)} className="text-[10px] tracking-luxe uppercase text-white/60 hover:text-gold">Edit</button>
                  <button onClick={() => remove(i.id)} className="text-red-400 p-1"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Field({ label, full, children }) { return <div className={full ? "md:col-span-2" : ""}><label className="eyebrow block mb-2">{label}</label>{children}</div>; }
